import React, { ReactNode, useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const IMG_PADDING = 12;

export interface ParallaxContentProps {
  imgUrl: string;
  subheading: any;
  subheading2?: any;
  heading: any;
  children: ReactNode;
  button1?: string;
  button1Url?: string;
  button2?: string;
  button2Url?: string;
  rounded?: boolean;
}

export interface ParallaxVideoProps {
  videoUrl: string;
  subheading: any;
  subheading2?: any;
  heading: any;
  children: ReactNode;
  button1?: string;
  button1Url?: string;
  button2?: string;
  button2Url?: string;
  rounded?: boolean;
}

export interface ParallaxCardsProps {
  cards: Array<{
    title: string;
    description: string;
    icon?: ReactNode;
    bgColor?: string;
  }>;
  imgUrl?: string;
  subheading: any;
  subheading2?: any;
  heading: any;
  children: ReactNode;
  rounded?: boolean;
}


export const ParallaxContentWithChildren = (props: ParallaxContentProps) => {
  const { imgUrl, subheading, heading, children, button1, button2, rounded } = props;
  return (
    <div
      style={{
        paddingLeft: rounded ? IMG_PADDING : 0,
        paddingRight: rounded ? IMG_PADDING : 0,
      }}
    >
      <div className="relative">
        <OverlayChildren {...props} />
      </div>
      {/* {children} */}
    </div>
  );
};

export const ParallaxContent = (props: ParallaxContentProps) => {
  const { imgUrl, subheading, heading, children, button1, button2, rounded } = props;
  return (
    <div
      style={{
        paddingLeft: rounded ? IMG_PADDING : 0,
        paddingRight: rounded ? IMG_PADDING : 0,
      }}
    >
      <div className="relative h-[100vh] overflow-hidden">
        <StickyImage {...props} />
        <OverlayCopy {...props} />
      </div>
      {children}
    </div>
  );
};

export const ParallaxVideo = (props: ParallaxVideoProps) => {
  const { videoUrl, subheading, heading, children, button1, button2, rounded } = props;
  return (
    <div
      style={{
        paddingLeft: rounded ? IMG_PADDING : 0,
        paddingRight: rounded ? IMG_PADDING : 0,
      }}
    >
      <div className="relative h-[100vh]">
        <StickyVideo {...props} />
        <OverlayCopyVideo {...props} />
      </div>
      {children}
    </div>
  );
};

export const ParallaxCards = (props: ParallaxCardsProps) => {
  const { cards, subheading, heading, children, rounded } = props;
  return (
    <div
      style={{
        paddingLeft: rounded ? IMG_PADDING : 0,
        paddingRight: rounded ? IMG_PADDING : 0,
      }}
    >
      <div className="relative h-[120vh]">
        <StickyCards {...props} />
        <OverlayCopyCards {...props} />
      </div>
      {children}
    </div>
  );
};

export const StickyImage = ({ imgUrl, rounded=true }: ParallaxContentProps) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["end end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.1, 0.5, 0.75, 1], [1, 1.1, 1.2, 1.3, 1.35]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <motion.div
      style={{
        backgroundImage: `url(${imgUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: `calc(100vh - ${rounded ? IMG_PADDING * 2 : 0}px)`,
        top: rounded ? IMG_PADDING : 0,
        scale: rounded ? scale : 1,
      }}
      ref={targetRef}
      className={`sticky z-0 overflow-hidden`}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/50"
        style={{
          opacity,
        }}
      />
    </motion.div>
  );
};

export const StickyVideo = ({ videoUrl, rounded }: ParallaxVideoProps) => {
  const targetRef = useRef(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef(null);

  const { scrollYProgress: stickyProgress } = useScroll({
    target: targetRef,
    offset: ["end end", "end start"],
  });

  const { scrollYProgress: videoProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(stickyProgress, [0, 1], [1, 0.85]);
  const opacity = useTransform(stickyProgress, [0, 1], [1, 0]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Force eager loading
    video.preload = "auto";
    video.load();

    // Optional: decode early so it’s fully ready
    video
      .play()
      .then(() => {
        video.pause(); // Just enough to decode and buffer
        video.currentTime = 0;
      })
      .catch(() => {
        // Ignore autoplay blocking errors since it's muted anyway
      });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const unsubscribe = videoProgress.on("change", (latest) => {
        if (video.duration && isFinite(video.duration)) {
          const clampedProgress = Math.max(0, Math.min(1, latest));
          video.currentTime = clampedProgress * video.duration;
        }
      });

      return unsubscribe;
    };

    if (video.readyState >= 1) {
      const unsubscribe = handleLoadedMetadata();
      return unsubscribe;
    } else {
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [videoProgress]);

  return (
    <div ref={containerRef}>
      <motion.div
        style={{
          height: `calc(100vh - ${rounded ? IMG_PADDING * 2 : 0}px)`,
          top: rounded ? IMG_PADDING : 0,
          scale: rounded ? scale : 1,
        }}
        ref={targetRef}
        className={`sticky z-0 overflow-hidden bg-black ${rounded && "rounded-3xl"}`}
      >
        <video
          src={videoUrl}
          muted
          playsInline
          preload="auto"
          style={{ display: "none" }}
          autoPlay
          loop
        />
        <video
          ref={videoRef}
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
          preload="auto"
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70"
          style={{
            opacity,
          }}
        />
      </motion.div>
    </div>
  );
};

export const StickyCards = ({ cards, rounded, imgUrl }: ParallaxCardsProps) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
    // offset: ["end end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  const cardOpacity = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.9, 1],
    [0, 0, 1, 0, 0]
  );
  const cardY = useTransform(scrollYProgress, [0, 0.2, 0.4, 0.6, 0.8, 1], [600, 400, 200, -100, -200, 300]);


  return (
    <motion.div
      style={{
        height: `calc(100vh - ${rounded ? IMG_PADDING * 2 : 0}px)`,
        top: rounded ? IMG_PADDING : 0,
        scale: rounded ? scale : 1,
      }}

      className={`sticky z-0 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${rounded && "rounded-3xl"}`}
    >
      <div style={{
        backgroundImage: `url(${imgUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
        className="absolute inset-0 flex items-center justify-center p-8 opacity-20 blur-lg"
      ></div>
      {/* <div className="absolute inset-0 flex items-center justify-center p-8">
        <motion.div ref={targetRef} style={{opacity: cardOpacity, y: cardY }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl w-full">
          {cards.map((card, index) =>
          (
            <ParallaxCard
              key={index}
              card={card}
              index={index}
              scrollYProgress={scrollYProgress}
            />
          )
          )}
        </motion.div>
      </div> */}
    </motion.div>
  );
};

export const OverlayChildren = (props: ParallaxContentProps) => {
  const { subheading, heading, button1, button2, children } = props;
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [250, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0.2, 1, 0.2]);

  return (
    <motion.div
      style={{
        y,
        opacity,
      }}
      ref={targetRef}
      // className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center text-white px-4"
    >
      {children}
    </motion.div>
  );
};

export const OverlayCopy = ({ subheading, subheading2, heading, button1, button1Url, button2, button2Url }: ParallaxContentProps) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [250, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.52, 0.6, 0.75], [0, 0, 1, 1, 1]);

  return (
    <motion.div
      style={{
        y: 1,
        opacity,
      }}
      ref={targetRef}
      className="absolute left-0 top-0 flex h-screen w-full flex-col items-center justify-center text-white px-4 md:px-[20%] backdrop-blur-3xl"
    >
      <p className="mb-1 text-center text-xl md:mb-2 md:text-3xl font-medium tracking-wide">
        {subheading}
      </p>
      {subheading2 && <p className="mb-1 text-center text-lg md:mb-2 md:text-2xl font-medium tracking-wide">
        {subheading2}
      </p>}
      <p className="text-center text-4xl font-bold mt-1  md:mt-2 md:text-7xl mb-8 max-w-4xl leading-tight">
        {heading}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 mt-4">
        {button1 && (
          <button onClick={() => {window.location.href = button1Url || "/"}} className="bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl">
            {button1}
          </button>
        )}
        {button2 && (
          <button onClick={() => {window.location.href = button2Url || "/"}} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 hover:scale-105 shadow-xl">
            {button2}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const OverlayCopyVideo = ({ subheading, subheading2, heading, button1, button2 }: ParallaxVideoProps) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [250, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0, 1, 0]);

  return (
    <motion.div
      style={{
        y,
        opacity,
      }}
      ref={targetRef}
      className="absolute left-0 top-0 flex h-screen w-full flex-col items-center justify-center text-white px-4 md:px-[20%]"
    >
      <p className="mb-1 text-center text-xl md:mb-2 md:text-3xl font-medium tracking-wide">
        {subheading}
      </p>
      {subheading2 && <p className="mb-1 text-center text-lg md:mb-2 md:text-2xl font-medium tracking-wide">
        {subheading2}
      </p>}
      <p className="text-center text-4xl font-bold mt-1  md:mt-2 md:text-7xl mb-8 max-w-4xl leading-tight">
        {heading}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 mt-4">
        {button1 && (
          <button className="bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl">
            {button1}
          </button>
        )}
        {button2 && (
          <button className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 hover:scale-105 shadow-xl">
            {button2}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const OverlayCopyCards = ({ subheading, subheading2, heading, cards }: ParallaxCardsProps) => {
  // const { subheading, heading } = props;
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [500, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75, 0.8], [0, 1, 1, 0]);

  return (
    <motion.div
      style={{
        y,
        opacity,
      }}
      ref={targetRef}
      className="absolute z-0 left-0 top-0 flex h-screen w-full flex-col items-center justify-center text-white px-4 pointer-events-none"
    >
      <p className="mb-1 text-center text-xl md:mb-2 md:text-3xl font-medium tracking-wide">
        {subheading}
      </p>
      {subheading2 && <p className="mb-1 text-center text-lg md:mb-2 md:text-2xl font-medium tracking-wide">
        {subheading2}
      </p>}
      <p className="text-center text-4xl font-bold mt-1  md:mt-2 md:text-7xl mb-8 max-w-4xl leading-tight">
        {heading}
      </p>
      <div className="relative flex items-center justify-center p-8">
        <motion.div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl w-full">
          {cards.map((card, index) =>
          (
            <ParallaxCard
              key={index}
              card={card}
              index={index}
              scrollYProgress={scrollYProgress}
            />
          )
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

const ParallaxCard = ({
  index,
  card,
  scrollYProgress,
}: {
  index: number;
  card: ParallaxCardsProps["cards"][0];
  scrollYProgress: ReturnType<typeof useScroll>["scrollYProgress"];
}) => {
  const delay = index * 0.05;
  const cardOpacity = useTransform(
    scrollYProgress,
    [0 + delay, 0.2 + delay, 0.5 + delay, 0.7 + delay],
    [0, 0, 1, 0]
  );

  const cardY = useTransform(
    scrollYProgress,
    [0 + delay, 0.2 + delay, 0.5 + delay],
    [180, 100, -80]
  );

  return (
    <motion.div
      style={{ opacity: 1, y: 50 }}
      className={`${card.bgColor || "bg-white/10"
        } relative backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105`}
    >
      {card.icon && <div className="mb-4 text-white">{card.icon}</div>}
      <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
      <p className="text-gray-300 text-sm leading-relaxed">
        {card.description}
      </p>
    </motion.div>
  );
};

export const ExampleContent = () => (
  <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-4 pb-24 pt-12 md:grid-cols-12">
    <h2 className="col-span-1 text-3xl font-bold md:col-span-4 text-white">
      Lorem ipsum dolor sit amet consectetur adipisicing elit.
    </h2>
    <div className="col-span-1 md:col-span-8">
      <p className="mb-4 text-xl text-neutral-300 md:text-2xl">
        Lorem ipsum dolor sit amet consectetur adipisicing elit. Quasi,
        blanditiis soluta eius quam modi aliquam quaerat odit deleniti minima
        maiores voluptate est ut saepe accusantium maxime doloremque nulla
        consectetur possimus.
      </p>
      <p className="mb-8 text-xl text-neutral-300 md:text-2xl">
        Lorem ipsum dolor sit amet consectetur adipisicing elit. Accusantium
        reiciendis blanditiis aliquam aut fugit sint.
      </p>
      <button className="w-full rounded-xl bg-neutral-200 px-9 py-4 text-xl text-black transition-all duration-300 hover:bg-neutral-700 hover:scale-105 shadow-lg md:w-fit">
        Learn more <ArrowRight className="inline" />
      </button>
    </div>
  </div>
);
